import { AppLink } from "../foundations/AppLink.js";
import { cn, tv } from "../foundations/cn.js";
import { FOCUS_RING } from "../foundations/interaction.js";

/** One tab of a TabNav: a page, or a view of one, with its own URL. */
export interface TabNavItem {
  id: string;
  label: string;
  href: string;
  /** The tab showing; marked aria-current=page. */
  current: boolean;
}

const tabStyles = tv({
  base: [
    "inline-flex shrink-0 cursor-pointer items-center justify-center whitespace-nowrap font-body",
    FOCUS_RING,
  ],
  variants: {
    variant: {
      /** Sibling pages of one place (Vault / Wallet / Settings / History). */
      segmented:
        "h-control-sm rounded-control px-4 text-body-b2-strong text-text-tertiary hover:text-text-secondary aria-[current=page]:bg-bg-raised aria-[current=page]:text-text-primary aria-[current=page]:shadow-[var(--theme-surface-shadow)]",
      /** A panel's sub-tabs (Settings' Profile / Shipping / Verification). */
      chips:
        "h-control-sm rounded-control border border-transparent px-3.5 text-body-b2-strong text-text-secondary hover:text-text-primary aria-[current=page]:border-accent-default aria-[current=page]:bg-accent-subtle aria-[current=page]:text-accent-text",
    },
  },
  defaultVariants: { variant: "segmented" },
});

export interface TabNavProps {
  /** The nav's accessible name: "Account", "Settings". */
  label: string;
  items: readonly TabNavItem[];
  variant?: "segmented" | "chips";
  className?: string;
}

/**
 * Tabs that are links: each view has its own URL, so it can be shared, reloaded and reached from
 * elsewhere (Deposit lands on Wallet). A named nav, the current tab aria-current=page; on phones
 * the row scrolls sideways rather than wrapping.
 */
export function TabNav({ label, items, variant = "segmented", className }: TabNavProps) {
  return (
    <nav aria-label={label} className={cn("-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0", className)}>
      <ul
        className={cn(
          "inline-flex items-center gap-1",
          variant === "segmented" && "rounded-control border border-border-subtle bg-bg-inset p-1",
        )}
      >
        {items.map((item) => (
          <li key={item.id} className="flex">
            <AppLink
              href={item.href}
              aria-current={item.current ? "page" : undefined}
              className={tabStyles({ variant })}
            >
              {item.label}
            </AppLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
